import { FC } from "react";
import styled from "styled-components";
import { Button } from "src/components/ui";
import { Feedback } from "src/types/feedback";
import { formatDate } from "src/utils";

const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
`;

const ModalContent = styled.div`
	background: white;
	border-radius: 16px;
	width: 100%;
	max-width: 600px;
	padding: 24px;
`;

const ModalHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;

	h2 {
		font-size: 20px;
		font-weight: 600;
		color: ${(props) => props.theme.colors.text_01};
	}

	.actions {
		display: flex;
		gap: 16px;
		align-items: center;
	}
`;

const UserInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 24px;

	img {
		width: 48px;
		height: 48px;
		border-radius: 50%;
		object-fit: cover;
	}

	.name {
		font-size: 18px;
		font-weight: 500;
		color: ${(props) => props.theme.colors.text_01};
	}
`;

const Section = styled.div`
	margin-bottom: 24px;

	h3 {
		font-size: 16px;
		font-weight: 500;
		color: ${(props) => props.theme.colors.text_02};
		margin-bottom: 8px;
	}

	.feedback-status {
		display: inline-block;
		padding: 6px 12px;
		border-radius: 16px;
		font-weight: 500;

		&.very-satisfied {
			background: #e6f4ea;
			color: #1e8e3e;
		}

		&.satisfied {
			background: #e8f0fe;
			color: #1a73e8;
		}

		&.neutral {
			background: #f1f3f4;
			color: #5f6368;
		}

		&.unsatisfied {
			background: #fef7e6;
			color: #f29d38;
		}

		&.very-unsatisfied {
			background: #fce8e8;
			color: #d93025;
		}
	}

	.remark {
		color: ${(props) => props.theme.colors.text_01};
		line-height: 1.5;
	}
`;

const CloseButton = styled(Button)`
	background: ${(props) => props.theme.colors.purple};
	color: white;
	padding: 8px 24px;
	border-radius: 8px;

	&:hover {
		opacity: 0.9;
	}
`;

interface Props {
	feedback: Feedback;
	onClose: () => void;
}

export const FeedbackDetailsModal: FC<Props> = ({ feedback, onClose }) => {
	const rate = feedback?.emojiRating;
	return (
		<ModalOverlay onClick={onClose}>
			<ModalContent onClick={(e) => e.stopPropagation()}>
				<ModalHeader>
					<h2>User Feedback</h2>
					<div className='actions'>
						<svg
							width='24'
							height='24'
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							style={{ cursor: "pointer" }}
							onClick={onClose}
						>
							<path d='M18 6L6 18M6 6l12 12' />
						</svg>
					</div>
				</ModalHeader>

				<UserInfo>
					<img
						src={feedback.display_picture}
						alt='User'
						className='rounded-full object-cover w-10 h-10'
					/>
					<div className='user-details'>
						<div className='name'>{`${feedback.first_name} ${feedback.last_name}`}</div>
						<div className='contact'>
							<div>{feedback.email}</div>
							<div>{feedback.phone}</div>
						</div>
					</div>
				</UserInfo>

				<div className='flex justify-between items-center mb-4'>
					<div>
						<h3>
							Rating:{" "}
							<span
								className={`${
									rate.toLowerCase() === "satisfied"
										? "text-green-600"
										: rate.toLowerCase() === "unsatisfied"
										? "text-red-600"
										: rate.toLowerCase() === "very unsatisfied"
										? "text-purple-600"
										: "text-gray-600"
								} font-bold`}
							>
								{rate}
							</span>
						</h3>
					</div>
				</div>

				<Section>
					<h3>Feedback</h3>
					<p className='feedback-text bg-gray-100 p-4 rounded-md'>
						{feedback.feedback}
					</p>
				</Section>

				<Section>
					<h3>Submitted</h3>
					<p>{formatDate(feedback.submitted_at)}</p>
				</Section>

				<div style={{ textAlign: "right" }}>
					<CloseButton onClick={onClose}>Close</CloseButton>
				</div>
			</ModalContent>
		</ModalOverlay>
	);
};
